import type { MailingAddress } from "@shopify/hydrogen-react/storefront-api-types";
import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import type { ICaaCustomerAddress } from "@repo/shopify/queries/customer-account-gql";

import Button from "@atoms/Button";

import AddressForm from "@organisms/Address/AddressForm";
import AddressFormIntl from "@organisms/Address/AddressFormIntl";
import AddressFormJp from "@organisms/Address/AddressFormJp";
import AddressItem from "@organisms/Address/AddressItem";
import AddressItemJp from "@organisms/Address/AddressItemJp";
import AddressLayout from "@organisms/Address/AddressLayout";

import useAddresses from "@hooks/useAddresses";
import useLanguage from "@hooks/useLanguage";

import { useLayoutStore } from "@store/layout";

export default function Addresses() {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const { showFooter } = useLayoutStore();

  const { isPC } = useCheckDevice();

  const [showForm, setShowForm] = useState<boolean>(false);
  const [editableAddress, setEditableAddress] = useState<MailingAddress | ICaaCustomerAddress | undefined>();

  const { onCompletedMutation, defaultAddress, otherAddresses } = useAddresses();

  useEffect(() => {
    showFooter(true);
    window.scrollTo(0, 0);
  }, []);

  const handleCancelForm = () => {
    setShowForm(false);
    setEditableAddress(undefined);
  };

  const handleFormCompleted = async () => {
    await onCompletedMutation();
    setShowForm(false);
    setEditableAddress(undefined);
  };

  const openAddressForm = (item: MailingAddress | ICaaCustomerAddress | undefined) => {
    setShowForm(true);
    setEditableAddress(item);
  };

  return (
    <>
      <section className="pc:col-span-10 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-10">
        <div className="pc:col-span-3 col-span-8 flex h-fit flex-col gap-5">
          {isPC ? <h2 className="text-title">Addresses</h2> : null}
          <div className="flex flex-col gap-2.5">
            <p className="leading-5 wrap-break-word">
              <Trans i18nKey="addresses.description" />
            </p>
            <Button
              as="square"
              className="w-fit"
              onClick={() => {
                window.scrollTo(0, 0);
                setShowForm(true);
              }}
              variant="secondary"
            >
              {t("addresses.create_new_address")}
            </Button>
          </div>
        </div>

        <div className="pc:col-span-5 col-span-8 grid grid-cols-subgrid">
          {defaultAddress ? (
            <div className="pc:col-span-5 pc:gap-y-0 border-grey col-span-8 grid grid-cols-subgrid gap-y-2.5 border-b pb-5">
              {lang === "jp" ? (
                <AddressItemJp
                  address={defaultAddress as ICaaCustomerAddress}
                  isDefaultAddress
                  onCompletedMutation={onCompletedMutation}
                  openAddressForm={openAddressForm}
                />
              ) : (
                <AddressItem
                  address={defaultAddress as MailingAddress}
                  isDefaultAddress
                  onCompletedMutation={onCompletedMutation}
                  openAddressForm={openAddressForm}
                />
              )}
            </div>
          ) : null}

          {otherAddresses.length ? (
            <ul className="tnt-gap-x pc:col-span-5 col-span-8 grid grid-cols-subgrid gap-y-10 pt-5">
              {otherAddresses.map((node) => (
                <li className="pc:col-span-5 col-span-8 grid grid-cols-subgrid gap-y-2.5" key={node.id}>
                  {lang === "jp" ? (
                    <AddressItemJp
                      address={node as ICaaCustomerAddress}
                      isDefaultAddress={defaultAddress?.id === node.id}
                      onCompletedMutation={onCompletedMutation}
                      openAddressForm={openAddressForm}
                    />
                  ) : (
                    <AddressItem
                      address={node as MailingAddress}
                      isDefaultAddress={defaultAddress?.id === node.id}
                      onCompletedMutation={onCompletedMutation}
                      openAddressForm={openAddressForm}
                    />
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </section>

      {showForm ? (
        <AddressLayout handleCancel={handleCancelForm}>
          <div className="pc:pt-0 scrollbar-hide col-span-8 flex h-[calc(100dvh-10.5rem)] flex-col gap-5 overflow-y-scroll pt-4">
            <div className="flex flex-col gap-2.5">
              <h2 className="text-title">{editableAddress ? t("addresses.edit_address") : t("addresses.create_new_address")}</h2>
            </div>
            {lang === "ko" && (
              <AddressForm
                address={editableAddress as MailingAddress | undefined}
                isDefaultAddress={defaultAddress?.id === editableAddress?.id}
                onClickCancel={handleCancelForm}
                onCompletedMutation={handleFormCompleted}
              />
            )}
            {lang === "en" && (
              <AddressFormIntl
                address={editableAddress as MailingAddress | undefined}
                className="tnt-gap-x col-span-8 grid grid-cols-6 gap-y-2.5"
                isDefaultAddress={defaultAddress?.id === editableAddress?.id}
                isEmptyAddress={!defaultAddress?.id}
                onClickCancel={handleCancelForm}
                onCompletedMutation={handleFormCompleted}
              />
            )}
            {lang === "jp" && (
              <AddressFormJp
                address={editableAddress as ICaaCustomerAddress | undefined}
                className="tnt-gap-x col-span-8 grid grid-cols-6 gap-y-2.5"
                isDefaultAddress={defaultAddress?.id === editableAddress?.id}
                isEmptyAddress={!defaultAddress?.id}
                onClickCancel={handleCancelForm}
                onCompletedMutation={handleFormCompleted}
              />
            )}
          </div>
        </AddressLayout>
      ) : null}
    </>
  );
}
